/**
 * Compensation & activity — de beloningsafspraken per chatter naast wat hij in
 * het gekozen venster verkocht en verdiende.
 *
 * 28-10-2026 (Jordy): verhuisd van de Chatters-pagina naar Settings, en alleen
 * voor owner/admin. Managers, head chatters en chatters zien hier niets; de
 * backend weigert compensation-roster, commission en payout-wallet voor hen
 * met een 403, dus we vragen het voor hen ook niet op.
 *
 * Endpoints:
 *   GET   chatter-finance/compensation-roster        (afspraken + wallet)
 *   GET   chatter-finance/performance-summary        (cijfers van het venster)
 *   PATCH chatter-finance/commission/{id}            ("Save pay": % en grondslag)
 *   PATCH chatter-finance/payout-wallet/{id}         ("Save wallet": adres + netwerk)
 *
 * Head chatter, Referral % en Reports to staan hier alleen ter inzage. Die
 * wijzigt de owner via Chatters → Roster → Edit; "Save pay" stuurt ze bewust
 * niet mee, zodat er geen nieuwe referralperiode ontstaat.
 */
import { useState } from 'react'
import { ErrorNote, Loading, Panel, Seg, Table, Tag } from './ui'
import { Field, Modal } from './Modal'
import { useApi } from '../lib/useApi'
import { useActions, mut } from '../lib/actions'
import { isVolledigBeheer, useMij } from '../lib/rechten'
import { eur, num, pct } from '../lib/format'

/** Eén rij uit compensation-roster (CompensationRosterItem in de backend). */
interface Comp {
  chatter_id: number; name: string; active: boolean
  commission_pct: number | null; commission_pct_is_set: boolean
  default_commission_pct: number; commission_base: string
  is_lead: boolean; lead_override_pct: number | null
  lead_chatter_id: number | null
  payout_wallet_address: string | null; payout_wallet_network: string | null
}

/** De velden van een performance-summary-rij die deze tabel gebruikt. */
interface PerfRij {
  chatter_id: number | null
  sales?: number; gross?: number; net?: number
  total_earned?: number; open_commission?: number
}

const RANGES = [
  { value: '1', label: 'Today' }, { value: '7', label: 'Week' },
  { value: '30', label: 'Month' }, { value: '90', label: 'Quarter' },
  { value: '365', label: 'Year' },
]

/** De twee grondslagen die PATCH /commission accepteert, met de tekst van oud. */
const BASES = [
  { value: 'after_platform_model', label: 'Net — after platform + model fees' },
  { value: 'after_platform', label: 'Gross minus the 20% platform fee only' },
]

function baseLabel(v: string | null | undefined): string {
  return BASES.find((b) => b.value === v)?.label ?? (v || '—')
}

/** Een wallet hoort niet voluit in een overzicht: eerste 6, laatste 4. */
function kortAdres(adres: string | null | undefined, netwerk: string | null | undefined): string {
  const a = String(adres ?? '').trim()
  if (!a) return '—'
  const kort = a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a
  return `${kort} · ${netwerk || '—'}`
}

export function CompensatieInstellingen() {
  const mij = useMij()
  const mag = isVolledigBeheer(mij.data)
  const [days, setDays] = useState('30')
  const [editId, setEditId] = useState<number | null>(null)
  const [draft, setDraft] = useState({ pct: '', base: 'after_platform_model', addr: '', net: '' })
  const { run, busy, notify } = useActions()

  // Pas opvragen als vaststaat dat dit owner/admin is; anders geeft de server 403.
  const comp = useApi<Comp[]>(
    mag ? '/api/admin/chatter-finance/compensation-roster' : null, [mag])
  const sum = useApi<{ chatters?: PerfRij[] }>(
    mag
      ? `/api/admin/chatter-finance/performance-summary?platform=all&days=${days}`
      : null,
    [mag, days])

  if (mij.loading && !mij.data) return <Loading />
  if (!mag) {
    return (
      <Panel title="Compensation & activity">
        <div className="mut2">Owner/admin only.</div>
      </Panel>
    )
  }

  const rijen = comp.data ?? []
  const perf = sum.data?.chatters ?? []
  // De dialoog volgt de rij uit de laatst geladen lijst, zodat hij na een
  // opslag de nieuwe waarden toont.
  const edit = editId != null ? (rijen.find((x) => x.chatter_id === editId) ?? null) : null
  const naamVan = (id: number | null | undefined) =>
    id ? (rijen.find((x) => x.chatter_id === id)?.name ?? `#${id}`) : null

  const openEdit = (c: Comp) => {
    setDraft({
      pct: String(c.commission_pct ?? c.default_commission_pct),
      base: c.commission_base || 'after_platform_model',
      addr: c.payout_wallet_address ?? '',
      net: c.payout_wallet_network ?? '',
    })
    setEditId(c.chatter_id)
  }

  const pctGetal = Number(draft.pct)
  const pctGeldig = draft.pct.trim() !== '' && Number.isInteger(pctGetal)
    && pctGetal >= 0 && pctGetal <= 100

  const kopieer = async (tekst: string) => {
    try {
      await navigator.clipboard.writeText(tekst)
      notify('Wallet address copied.')
    } catch {
      notify('Could not copy — select the address and copy it by hand.', false)
    }
  }

  return (
    <>
      <div className="filters">
        <Seg options={RANGES} value={days} onChange={setDays} />
      </div>
      {/* Eén tabel: de afspraken (commissie, head chatter, wallet) naast de
          opbrengst van het venster, zodat je het verdiende bedrag ziet naast
          het percentage waar het uit volgt. */}
      <Panel
        title="Compensation & activity"
        desc="Agreements and earnings per chatter in this window. Owner/admin only."
        flush
      >
        {comp.loading ? <Loading /> : comp.error ? <ErrorNote error={comp.error} /> : (
          <>
            {sum.error ? <ErrorNote error={sum.error} /> : null}
            <Table cols={[
              { key: 'c', label: 'Chatter' }, { key: 's', label: 'Sales', num: true },
              { key: 'g', label: 'Gross', num: true }, { key: 'n', label: 'Net', num: true },
              { key: 'p', label: 'Commission %', num: true },
              { key: 'b', label: 'Commission base' },
              { key: 'hc', label: 'Head chatter' },
              { key: 'rp', label: 'Referral %', num: true },
              { key: 'rt', label: 'Reports to' },
              { key: 'e', label: 'Earned', num: true },
              { key: 'o', label: 'Open', num: true },
              { key: 'w', label: 'Payout wallet' },
              { key: 'a', label: 'Action' },
            ]} emptyText="No chatters.">
              {rijen.map((c) => {
                const w = perf.find((x) => Number(x.chatter_id) === c.chatter_id)
                const laadt = sum.loading && !sum.data
                return (
                  <tr key={c.chatter_id}>
                    <td>
                      <b>{c.name}</b>
                      {!c.active ? <> <Tag kind="mut">inactive</Tag></> : null}
                    </td>
                    <td className="num mono">{laadt ? '…' : num(w?.sales ?? 0)}</td>
                    <td className="num mono">{laadt ? '…' : eur(w?.gross ?? 0)}</td>
                    <td className="num mono">{laadt ? '…' : eur(w?.net ?? 0)}</td>
                    <td className="num mono">
                      {pct(c.commission_pct ?? c.default_commission_pct)}
                      {!c.commission_pct_is_set ? <span className="dim"> (default)</span> : null}
                    </td>
                    <td className="mut">{baseLabel(c.commission_base)}</td>
                    <td>{c.is_lead
                      ? <Tag kind="acc">Head</Tag>
                      : <span className="dim">—</span>}</td>
                    <td className="num mono">
                      {c.is_lead || c.lead_override_pct ? pct(c.lead_override_pct ?? 0) : '—'}
                    </td>
                    <td className="mut">{naamVan(c.lead_chatter_id) ?? '—'}</td>
                    <td className="num mono">{laadt ? '…' : eur(w?.total_earned ?? 0)}</td>
                    <td className="num mono">{laadt ? '…' : eur(w?.open_commission ?? 0)}</td>
                    <td className="mut2 mono" style={{ fontSize: 12 }}>
                      {kortAdres(c.payout_wallet_address, c.payout_wallet_network)}
                    </td>
                    <td>
                      <button className="btn ghost sm" type="button"
                              onClick={() => openEdit(c)}>Edit</button>
                    </td>
                  </tr>
                )
              })}
            </Table>
          </>
        )}
      </Panel>

      {edit ? (
        <Modal
          title={`Compensation — ${edit.name}`}
          desc="Pay and wallet are saved separately, same as the old dashboard."
          width={500}
          onClose={() => setEditId(null)}
        >
          <div className="mut2" style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Pay</div>
          <Field label="Commission %"
                 hint={edit.commission_pct_is_set
                   ? `Default: ${edit.default_commission_pct}%`
                   : `Not set — using the ${edit.default_commission_pct}% default. Saving sets it explicitly.`}>
            <input className="inp" type="number" min={0} max={100} step={1}
                   value={draft.pct}
                   onChange={(e) => setDraft((d) => ({ ...d, pct: e.target.value }))} />
          </Field>
          <Field label="Commission base">
            <select className="inp" value={draft.base}
                    onChange={(e) => setDraft((d) => ({ ...d, base: e.target.value }))}>
              {BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </Field>
          <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 14 }}>
            <button
              className="btn primary sm"
              type="button"
              disabled={!pctGeldig || busy === 'Save pay'}
              onClick={() => run({
                label: 'Save pay',
                // Alleen % en grondslag: head chatter, referral % en reports to
                // blijven ongemoeid (die velden laat de backend dan staan).
                request: mut.patch(
                  `/api/admin/chatter-finance/commission/${edit.chatter_id}`,
                  { commission_pct: pctGetal, commission_base: draft.base }),
                onDone: () => { comp.refetch(); sum.refetch() },
                successText: 'Pay saved.',
              })}
            >
              Save pay
            </button>
          </div>

          <div className="mut2" style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Payout wallet</div>
          <Field label="Wallet address" hint="Leave empty to remove the saved address.">
            <div className="row" style={{ gap: 6 }}>
              <input className="inp mono" style={{ flex: 1 }} placeholder="Wallet address"
                     value={draft.addr}
                     onChange={(e) => setDraft((d) => ({ ...d, addr: e.target.value }))} />
              <button className="btn ghost sm" type="button"
                      disabled={!edit.payout_wallet_address}
                      title={edit.payout_wallet_address ? 'Copy saved address' : 'No wallet saved yet'}
                      onClick={() => kopieer(edit.payout_wallet_address ?? '')}>
                Copy
              </button>
            </div>
          </Field>
          <Field label="Network">
            <input className="inp" placeholder="Network e.g. USDT-TRC20"
                   value={draft.net}
                   onChange={(e) => setDraft((d) => ({ ...d, net: e.target.value }))} />
          </Field>
          <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 14 }}>
            <button
              className="btn sm"
              type="button"
              disabled={busy === 'Save wallet'}
              onClick={() => run({
                label: 'Save wallet',
                request: mut.patch(
                  `/api/admin/chatter-finance/payout-wallet/${edit.chatter_id}`,
                  {
                    payout_wallet_address: draft.addr.trim(),
                    payout_wallet_network: draft.net.trim(),
                  }),
                onDone: () => comp.refetch(),
                successText: 'Wallet saved.',
              })}
            >
              Save wallet
            </button>
          </div>

          <div className="mut2" style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Head chatter</div>
          <div className="note" style={{ fontSize: 12 }}>
            <div>Head chatter: {edit.is_lead ? 'yes' : 'no'}</div>
            <div>Referral %: {edit.is_lead || edit.lead_override_pct ? pct(edit.lead_override_pct ?? 0) : '—'}</div>
            <div>Reports to: {naamVan(edit.lead_chatter_id) ?? '—'}</div>
            <div className="mut2" style={{ marginTop: 6 }}>
              Read-only here. The owner changes these via Chatters → Roster → Edit.
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  )
}
